import { ChangeDetectorRef, Component, ContentChild, ElementRef, EventEmitter, Input, NgZone, Output, TemplateRef, ViewChild, inject } from "@angular/core";
import { UntypedFormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";

import { throwError } from "rxjs";
import { catchError, finalize, first, tap } from "rxjs/operators";

import { DffError, isNumeric } from "@dffedb/util";
import { LoginService } from "@e-forsyning/common/auth";

import { LoginData } from "../login-data.model";
import { LoginHjaelpService } from "../login-hjaelp/login-hjaelp.service";
import { MatFormField } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatButton } from "@angular/material/button";
import { MatomoTracker } from "ngx-matomo-client";
import { CommonModule } from "@angular/common";
import { BiometricService } from "../biometrics/biometrics.service";

@Component({
    selector: "login-forbrugernr",
    templateUrl: "./login-forbrugernr.component.html",
    imports: [FormsModule, ReactiveFormsModule, MatFormField, MatInput, MatCheckbox, MatButton, CommonModule]
})
export class LoginForbrugernrComponent {
    @Input()
    public parent: UntypedFormGroup;

    @Input()
    public fejlBesked = "";

    @Output()
    public loginSucceeded = new EventEmitter<any>();

    @Output()
    public alphaKeyPressed = new EventEmitter<string>();

    @Output()
    public arbejderChange = new EventEmitter<boolean>();

    @ViewChild("forbrugerNrInput", { static: true }) public forbrugerNrInput: ElementRef;

    @ContentChild("biometricLoginButton") biometricTemplate?: TemplateRef<any>;

    public arbejder = false;

    private readonly matomoTracker = inject(MatomoTracker);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly ngZone = inject(NgZone);

    constructor(
        private readonly loginService: LoginService,
        private readonly loginHjaelpService: LoginHjaelpService,
        private readonly biometricService: BiometricService
    ) {}

    public focus(): void {}

    public loginClick(): void {
        const loginData: LoginData = {
            brugernavn: this.parent.get("forbrugerNrGroup.forbrugerNr").value,
            kodeord: this.parent.get("forbrugerNrGroup.kode").value,
            huskMig: this.parent.get("huskMig").value
        };
        this.onForbrugerLogin(loginData);
    }

    public onForbrugerLogin(credentials: LoginData): void {
        this.ngZone.run(() => {
            this.fejlBesked = "";
            this.setArbejder(true);
            this.changeDetectorRef.detectChanges();
        });
        this.loginService
            .login(credentials.brugernavn, credentials.kodeord, credentials.huskMig)
            .pipe(
                first(),
                tap(async (result) => {
                    if (result.success) {
                        const available = await this.biometricService.isAvailable();
                        if (available && !localStorage.getItem("biometricAsked")) {
                            try {
                                await this.biometricService.enableBiometricLogin(
                                    credentials.brugernavn,
                                    credentials.kodeord,
                                    "Aktiver biometrisk login",
                                    "Aktiver biometrisk login",
                                    "Bekræft din identitet for at gemme dine loginoplysninger sikkert. Herefter kan du logge ind hurtigere med finger- eller ansigtsgenkendelse."
                                );
                                localStorage.setItem("biometricAsked", "true");
                                this.loginSucceeded.emit();
                            } catch (err) {
                                localStorage.setItem("biometricAsked", "true");
                                this.loginSucceeded.emit();
                            }
                            this.matomoTracker.trackEvent("Authentication", "Login Success", "Forbrugernummer");
                        } else {
                            this.matomoTracker.trackEvent("Authentication", "Login Success", "Forbrugernummer");
                            this.loginSucceeded.emit();
                        }
                    } else {
                        this.matomoTracker.trackEvent("Authentication", "Login Failed", "Forbrugernummer - Invalid Credentials");
                        this.ngZone.run(() => {
                            this.fejlBesked = "Ugyldig forbrugernummer eller kode";
                            this.changeDetectorRef.detectChanges();
                        });
                    }
                }),
                catchError((error: DffError) => {
                    this.matomoTracker.trackEvent("Authentication", "Login Failed", "Forbrugernummer - Error");
                    this.ngZone.run(() => {
                        this.fejlBesked = error.friendlyMessage || error.message;
                        this.changeDetectorRef.detectChanges();
                    });
                    return throwError(error);
                }),
                finalize(() => {
                    this.ngZone.run(() => {
                        this.setArbejder(false);
                        this.changeDetectorRef.detectChanges();
                    });
                })
            )
            .subscribe();
    }

    private setArbejder(value: boolean): void {
        if (this.arbejder === value) {
            return;
        }

        this.arbejder = value;
        this.arbejderChange.emit(value);
    }

    public loginHjaelpClick(): void {
        this.loginHjaelpService.visLoginHjaelpDialog();
    }

    public onForbrugernummerKeypress(event: KeyboardEvent): void {
        const feltetErBlankt = !this.parent.get("forbrugerNrGroup.forbrugerNr").value;
        if (feltetErBlankt && !isNumeric(event.key)) {
            this.alphaKeyPressed.emit(event.key);
        }
    }
}
